import { ChangeDetectionStrategy, ChangeDetectorRef, Component, forwardRef, Input, OnInit } from "@angular/core";
import {
  AbstractControl,
  ControlValueAccessor,
  FormBuilder,
  NG_VALIDATORS,
  NG_VALUE_ACCESSOR,
  Validator,
  ValidatorFn,
  Validators
} from "@angular/forms";
import { EnumHelpers } from "@app/core/helpers";
import { ConditionMarketingActionInfo, CouponInfo } from "@app/models";
import { Unsubscriber } from "@app/shared/components";
import { Patterns } from "@app/shared/validators";
import { distinctUntilChanged } from "rxjs/operators";
import { TemplateDestinationType } from "clearline-api";
import { ItemWithLabel, WidgetType } from "clearline-common";
import { BehaviorSubject, Subject } from "rxjs";

export interface ActionDestinationForm {
  destinationType: TemplateDestinationType | keyof TemplateDestinationType;
  separateUrl: boolean;
  navigateUrl: string;
  navigateQrCodeUrl: string;
  couponId: string;
  widgetType: number;
}

@Component({
  selector: "app-action-destination",
  templateUrl: "./action-destination.component.html",
  styleUrls: ["./action-destination.component.scss"],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    { provide: NG_VALUE_ACCESSOR, useExisting: forwardRef(() => ActionDestinationComponent), multi: true },
    { provide: NG_VALIDATORS, useExisting: forwardRef(() => ActionDestinationComponent), multi: true }
  ]
})
export class ActionDestinationComponent extends Unsubscriber implements ControlValueAccessor, Validator, OnInit {
  @Input() marketingAppOptions: ConditionMarketingActionInfo[] = [];
  @Input() locationCouponsList: CouponInfo[] = null;
  @Input() showDestinationOptions = true;
  @Input() hasNoneOption = false;
  @Input() destinationTypes: TemplateDestinationType[];
  @Input() showFormLabels = true;
  @Input() allowSeparateUrl = false;

  @Input() set isCouponDisabled(value: boolean) {
    this._isCouponDisabled = value;
    this.updateMarketingAppOptions();
  }

  @Input() set isRequired(value: boolean) {
    this._isRequired = value;
    this.updateValidators();
  }

  private readonly digitalCouponWidgetType = WidgetType.IssueCoupon;
  readonly TemplateDestinationType = TemplateDestinationType;
  readonly navigateUrlValidators = [...this.defaultUrlValidators];

  protected navigateQrCodeUrlValidators = [...this.defaultUrlValidators, Validators.required];

  protected form = this.fb.group({
    destinationType: [TemplateDestinationType.Url],
    separateUrl: [false],
    navigateUrl: [null],
    navigateQrCodeUrl: [null],
    couponId: [null],
    widgetType: [null]
  });
  protected makeUrlTouched$ = new Subject<boolean>();

  protected radioButtons: ItemWithLabel<TemplateDestinationType>[] = [];
  protected checkbox = false;

  protected filteredMarketingAppOptions$ = new BehaviorSubject<ConditionMarketingActionInfo[]>([]);
  protected disabled$ = new BehaviorSubject<boolean>(false);
  protected _isRequired = false;
  protected _isSeparateUrl = false;
  protected _isCouponDisabled = false;

  protected onTouched: any = () => null;
  protected onChange: any = () => null;

  constructor(private fb: FormBuilder, private cdr: ChangeDetectorRef) {
    super();
  }

  get isRequired(): boolean {
    return this._isRequired;
  }

  get isSeparateUrl(): boolean {
    return this._isSeparateUrl;
  }

  get defaultUrlValidators(): ValidatorFn[] {
    return [Validators.pattern(Patterns.url)];
  }

  get destinationTypeValue(): TemplateDestinationType {
    return this.form.controls.destinationType.value;
  }

  get isWidgetDigitalCoupon(): boolean {
    return this.form.controls.widgetType.value === this.digitalCouponWidgetType;
  }

  get isSingleDestinationType(): boolean {
    return this.radioButtons.length === 1;
  }

  private updateMarketingAppOptions(): void {
    if (this._isCouponDisabled) {
      this.marketingAppOptions = this.marketingAppOptions.map((option: ConditionMarketingActionInfo) => {
        if (option.widgetType === WidgetType.IssueCoupon) {
          return { ...option, disabled: true };
        }
        return option;
      });
    }
    this.filteredMarketingAppOptions$.next(this.marketingAppOptions);
  }

  ngOnInit(): void {
    this.radioButtons = this.getRadioButtons(this.hasNoneOption);

    this.updateMarketingAppOptions();

    this.sub = this.form.controls.destinationType.valueChanges.subscribe(() => {
      this.updateValidators();
    });

    this.sub = this.form.controls.separateUrl.valueChanges.subscribe((value) => {
      this._isSeparateUrl = value;
    });

    this.sub = this.form.controls.couponId.valueChanges.subscribe(() => {
      this.cdr.markForCheck();
    });

    this.sub = this.form.controls.widgetType.valueChanges.subscribe((widgetType) => {
      this.updateValidators();
    });

    this.sub = this.form.valueChanges.pipe(distinctUntilChanged((a, b) => JSON.stringify(a) === JSON.stringify(b))).subscribe(() => {
      this.onModelChange();
      this.onTouched();
    });
  }

  markAllAsTouched(): void {
    // todo: Refactor component to avoid parent using this method. Write a proper editable URL component to use instead app-safe-url-form-control
    this.form.markAllAsTouched();
    this.makeUrlTouched$.next(true);
  }

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled$.next(isDisabled);
  }

  writeValue(value: ActionDestinationForm | null): void {
    const defaultDestinationType: TemplateDestinationType = this.getDefaultDestinationType();

    if (value === null) {
      this.form.reset({
        destinationType: defaultDestinationType,
        separateUrl: false,
        navigateUrl: null,
        navigateQrCodeUrl: null,
        couponId: null,
        widgetType: null
      });
    } else {
      const { navigateUrl, separateUrl, navigateQrCodeUrl, destinationType, widgetType, couponId } = value;
      const isCoupon = destinationType ? TemplateDestinationType[destinationType] === TemplateDestinationType.Coupon : false;

      this.form.setValue({
        destinationType: isCoupon
          ? TemplateDestinationType.Widget
          : destinationType
          ? TemplateDestinationType[destinationType]
          : defaultDestinationType,
        separateUrl: !!separateUrl,
        navigateUrl: navigateUrl ?? "",
        navigateQrCodeUrl: navigateQrCodeUrl ?? "",
        widgetType: isCoupon ? this.digitalCouponWidgetType : widgetType ?? "",
        couponId: couponId ?? ""
      });
    }
    this.updateValidators();
  }

  onModelChange() {
    const { destinationType, separateUrl, navigateUrl, navigateQrCodeUrl, widgetType, couponId } = this.form.getRawValue();
    const isCoupon = destinationType === TemplateDestinationType.Widget && widgetType === this.digitalCouponWidgetType;

    this.onChange({
      destinationType: EnumHelpers.getKeyNameByValue(TemplateDestinationType, isCoupon ? TemplateDestinationType.Coupon : destinationType),
      separateUrl,
      navigateUrl,
      navigateQrCodeUrl,
      couponId,
      widgetType
    } as ActionDestinationForm);
  }

  validate(control: AbstractControl): { [key: string]: any } | null {
    return this.form.valid ? null : { actionInvalid: true };
  }

  private getRadioButtons(hasNoneOption: boolean): ItemWithLabel<TemplateDestinationType>[] {
    const baseRadioButtons: ItemWithLabel<TemplateDestinationType>[] = this.getBaseRadioButtons();

    if (hasNoneOption) {
      return [{ label: "templates.createPage.noneDestination", value: TemplateDestinationType.None, disabled: false }, ...baseRadioButtons];
    }

    return baseRadioButtons;
  }

  private getBaseRadioButtons(): ItemWithLabel<TemplateDestinationType>[] {
    const allButtons: ItemWithLabel<TemplateDestinationType>[] = [
      { label: "templates.createPage.customUrl", value: TemplateDestinationType.Url, disabled: false },
      { label: "templates.createPage.marketingApp", value: TemplateDestinationType.Widget, disabled: false }
    ];

    if (this.destinationTypes?.length) {
      return allButtons.filter((button) => this.destinationTypes.includes(button.value));
    }

    return allButtons;
  }

  private updateValidators(): void {
    const { destinationType, widgetType } = this.form.getRawValue();

    if (destinationType === TemplateDestinationType.None) {
      this.form.get("widgetType").setValidators([]);
      this.form.get("navigateQrCodeUrl").setValidators([]);
      this.form.get("couponId").setValidators([]);
    }

    if (destinationType === TemplateDestinationType.Widget) {
      const widgetTypeValidators = this._isRequired ? [Validators.required] : null;

      this.form.get("widgetType").setValidators(widgetTypeValidators);
      this.form.get("navigateQrCodeUrl").setValidators([]);

      if (widgetType === this.digitalCouponWidgetType) {
        this.form.controls.couponId.setValidators([Validators.required]);
      } else {
        this.form.controls.couponId.setValidators([]);
      }
    }

    if (destinationType === TemplateDestinationType.Url) {
      this.navigateQrCodeUrlValidators = this._isRequired ? [...this.defaultUrlValidators, Validators.required] : this.defaultUrlValidators;

      this.form.get("navigateQrCodeUrl").setValidators([...this.navigateQrCodeUrlValidators]);
      this.form.get("widgetType").setValidators([]);
      this.form.get("couponId").setValidators([]);
    }

    if (destinationType === TemplateDestinationType.Coupon) {
      const couponIdValidators = this._isRequired ? [Validators.required] : null;

      this.form.get("couponId").setValidators(couponIdValidators);
      this.form.get("navigateQrCodeUrl").setValidators([]);
      this.form.get("widgetType").setValidators([]);
    }

    this.form.get("widgetType").updateValueAndValidity({ emitEvent: false });
    this.form.get("navigateQrCodeUrl").updateValueAndValidity({ emitEvent: false });
    this.form.get("couponId").updateValueAndValidity({ emitEvent: false });
    this.form.updateValueAndValidity({ emitEvent: false });

    this.onModelChange();
  }

  private getDefaultDestinationType(): TemplateDestinationType {
    if (this.isSingleDestinationType) {
      return this.radioButtons[0].value;
    }
    return this.hasNoneOption ? TemplateDestinationType.None : TemplateDestinationType.Url;
  }
}
